/**
 * Repleo Theme - Global JavaScript
 * Vanilla JS, no dependencies. Kept under 16KB minified.
 */

(function () {
  'use strict';

  /* ==========================================================================
     Utility helpers
     ========================================================================== */

  function qs(selector, scope) {
    return (scope || document).querySelector(selector);
  }

  function qsa(selector, scope) {
    return Array.from((scope || document).querySelectorAll(selector));
  }

  function on(el, event, handler, opts) {
    if (el) el.addEventListener(event, handler, opts || false);
  }

  function off(el, event, handler) {
    if (el) el.removeEventListener(event, handler);
  }

  var _focusTrapHandler = null;
  var _focusTrapContainer = null;
  function trapFocus(container) {
    // Remove previous trap to avoid duplicate listeners
    if (_focusTrapHandler && _focusTrapContainer) {
      off(_focusTrapContainer, 'keydown', _focusTrapHandler);
    }
    _focusTrapContainer = container;
    _focusTrapHandler = function (e) {
      if (e.key !== 'Tab') return;
      // Re-query on each Tab press to handle dynamic content (e.g. cart updates)
      var focusable = qsa('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])', container);
      if (!focusable.length) return;
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    on(container, 'keydown', _focusTrapHandler);
  }

  /* Accessibility announcer for screen readers */
  var _liveRegion = null;
  function announce(message) {
    if (!_liveRegion) {
      _liveRegion = document.createElement('div');
      _liveRegion.setAttribute('aria-live', 'polite');
      _liveRegion.setAttribute('aria-atomic', 'true');
      _liveRegion.className = 'visually-hidden';
      document.body.appendChild(_liveRegion);
    }
    _liveRegion.textContent = '';
    setTimeout(function () { _liveRegion.textContent = message; }, 100);
  }

  /* ==========================================================================
     Cart API helpers
     ========================================================================== */

  var CartAPI = {
    get: function () {
      return fetch('/cart.js', { credentials: 'same-origin' })
        .then(function (r) { return r.json(); });
    },

    add: function (items) {
      // C98 (2026-10-07): "?appstle=true" makes Appstle's fetch/XHR override skip this request. Without it Appstle
      // rewrites selling_plan from its own (hidden) widget = one-time, so "Subscribe & Save" landed as a one-time item.
      return fetch('/cart/add.js?appstle=true', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: items })
      }).then(function (r) {
        if (!r.ok) {
          return r.json().catch(function () { return {}; }).then(function (data) {
            var msg = data.description || data.message || r.statusText;
            console.error('Cart add error:', r.status, msg, 'Items:', items);
            throw new Error(msg);
          });
        }
        return r.json();
      });
    },

    change: function (key, quantity) {
      return fetch('/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: quantity })
      }).then(function (r) {
        if (!r.ok) throw new Error(r.statusText);
        return r.json();
      });
    },

    update: function (updates) {
      return fetch('/cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: updates })
      }).then(function (r) { return r.json(); });
    }
  };

  /* ==========================================================================
     Mobile menu
     ========================================================================== */

  function initMobileMenu() {
    var toggle = qs('[data-menu-toggle]');
    var menu = qs('.mobile-menu');
    var body = document.body;
    if (!toggle || !menu) return;

    var overlay = menu.previousElementSibling;
    if (!overlay || !overlay.classList.contains('header__mobile-overlay')) {
      overlay = null;
    }

    function openMenu() {
      menu.hidden = false;
      menu.setAttribute('aria-hidden', 'false');
      // Delay class add for CSS transition to work
      requestAnimationFrame(function () {
        menu.classList.add('is-open');
      });
      toggle.setAttribute('aria-expanded', 'true');
      // Scroll-lock: use position:fixed to preserve scroll position (prevents iOS scroll-to-top)
      var scrollY = window.scrollY || window.pageYOffset;
      body.dataset.menuScrollY = scrollY;
      body.style.position = 'fixed';
      body.style.width = '100%';
      body.style.top = '-' + scrollY + 'px';
      // Swap hamburger / close icons
      var menuIcon = qs('.header__menu-icon', toggle);
      var closeIcon = qs('.header__close-icon', toggle);
      if (menuIcon) menuIcon.hidden = true;
      if (closeIcon) closeIcon.hidden = false;
      if (overlay) overlay.classList.add('is-open');
      trapFocus(menu);
    }

    function closeMenu() {
      menu.classList.remove('is-open');
      menu.setAttribute('aria-hidden', 'true');
      toggle.setAttribute('aria-expanded', 'false');
      // Restore scroll position (matches position:fixed scroll-lock in openMenu)
      var scrollY = parseInt(body.dataset.menuScrollY || '0', 10);
      body.style.position = '';
      body.style.width = '';
      body.style.top = '';
      window.scrollTo(0, scrollY);
      // Swap icons back
      var menuIcon = qs('.header__menu-icon', toggle);
      var closeIcon = qs('.header__close-icon', toggle);
      if (menuIcon) menuIcon.hidden = false;
      if (closeIcon) closeIcon.hidden = true;
      if (overlay) overlay.classList.remove('is-open');
      // Wait for transition then hide
      setTimeout(function () {
        if (!menu.classList.contains('is-open')) {
          menu.hidden = true;
        }
      }, 350);
      toggle.focus();
    }

    on(toggle, 'click', function () {
      var isOpen = menu.classList.contains('is-open');
      isOpen ? closeMenu() : openMenu();
    });

    // Close on overlay click
    if (overlay) on(overlay, 'click', closeMenu);

    // Close on Escape
    on(document, 'keydown', function (e) {
      if (e.key === 'Escape' && menu.classList.contains('is-open')) closeMenu();
    });

    // Close buttons inside menu
    qsa('[data-menu-close]', menu).forEach(function (btn) {
      on(btn, 'click', closeMenu);
    });
  }

  /* ==========================================================================
     Sticky header
     ========================================================================== */

  function initStickyHeader() {
    var header = qs('[data-header]');
    if (!header) return;

    var announcementBar = qs('[data-announcement-bar]');

    // Determine header theme based on which section is behind it
    // Sections declare their background with data-header-theme="dark" or "light"
    var sections = qsa('[data-header-theme]');

    // Default: check first section for initial state
    function applyTheme(theme) {
      header.classList.remove('header--over-dark', 'header--over-light');
      header.classList.add('header--over-' + theme);

      if (announcementBar) {
        announcementBar.classList.remove('announcement-bar--over-light', 'announcement-bar--over-dark');
        if (theme === 'light') {
          announcementBar.classList.add('announcement-bar--over-light');
        }
      }
    }

    // Synchronously compute the theme of the section currently behind the header.
    function computeTheme() {
      var headerBottom = header.getBoundingClientRect().bottom;
      for (var i = 0; i < sections.length; i++) {
        var rect = sections[i].getBoundingClientRect();
        if (rect.top <= headerBottom && rect.bottom > 0) {
          return sections[i].getAttribute('data-header-theme') || 'light';
        }
      }
      return 'light';
    }

    // Apply the correct theme immediately — transitions are still off (no
    // .header--ready yet), so there's no animated navy→light flash on load.
    // Enable transitions on the next frame for smooth scroll-driven changes.
    applyTheme(sections.length > 0 ? computeTheme() : 'light');
    requestAnimationFrame(function () { header.classList.add('header--ready'); });

    if (sections.length > 0) {
      // Use IntersectionObserver to detect which section is behind the header
      var headerHeight = header.offsetHeight + (announcementBar ? announcementBar.offsetHeight : 0);

      // rootMargin: trigger when section top hits header area
      var observer = new IntersectionObserver(function(entries) {
        // Find the entry that is most visible at the header position
        entries.forEach(function(entry) {
          if (entry.isIntersecting) {
            var theme = entry.target.getAttribute('data-header-theme') || 'light';
            applyTheme(theme);
          }
        });
      }, {
        rootMargin: '-1px 0px -' + Math.max(0, Math.round(window.innerHeight - headerHeight - 2)) + 'px 0px',
        threshold: 0
      });

      sections.forEach(function(section) {
        observer.observe(section);
      });

      // Also handle scroll for edge cases (very small sections)
      var ticking = false;
      on(window, 'scroll', function() {
        if (!ticking) {
          requestAnimationFrame(function() {
            // Find which section is at header position
            var headerBottom = header.getBoundingClientRect().bottom;
            var currentTheme = 'light'; // default
            for (var i = 0; i < sections.length; i++) {
              var rect = sections[i].getBoundingClientRect();
              if (rect.top <= headerBottom && rect.bottom > 0) {
                currentTheme = sections[i].getAttribute('data-header-theme') || 'light';
                break;
              }
            }
            applyTheme(currentTheme);
            ticking = false;
          });
          ticking = true;
        }
      }, { passive: true });
    } else {
      // No sections with data-header-theme — default to light on non-homepage
      applyTheme('light');
    }
  }

  /* ==========================================================================
     Country / language selector dropdown
     ========================================================================== */

  function initCountrySelector() {
    qsa('.country-selector').forEach(function (selector) {
      var btn = qs('.country-selector__toggle', selector);
      var dropdown = qs('.country-selector__dropdown', selector);
      if (!btn || !dropdown) return;

      on(btn, 'click', function (e) {
        e.stopPropagation();
        var isOpen = dropdown.hidden === false;
        dropdown.hidden = isOpen;
        btn.setAttribute('aria-expanded', String(!isOpen));
      });

      on(document, 'click', function (e) {
        if (!selector.contains(e.target)) {
          dropdown.hidden = true;
          btn.setAttribute('aria-expanded', 'false');
        }
      });

      on(document, 'keydown', function (e) {
        if (e.key === 'Escape' && !dropdown.hidden) {
          dropdown.hidden = true;
          btn.setAttribute('aria-expanded', 'false');
          btn.focus();
        }
      });
    });
  }

  /* ==========================================================================
     Cart drawer
     ========================================================================== */

  function initCartDrawer() {
    var drawer = qs('#cart-drawer');
    var overlay = qs('#cart-drawer-overlay');
    if (!drawer) return;

    function openDrawer() {
      drawer.classList.add('is-active');
      drawer.removeAttribute('inert');
      if (overlay) {
        overlay.classList.add('is-active');
        overlay.setAttribute('aria-hidden', 'false');
      }
      document.body.style.overflow = 'hidden';
      trapFocus(drawer);

      // Focus close button
      var closeBtn = qs('[data-cart-drawer-close]', drawer);
      if (closeBtn) setTimeout(function () { closeBtn.focus(); }, 100);
    }

    function closeDrawer() {
      drawer.classList.remove('is-active');
      drawer.setAttribute('inert', '');
      if (overlay) {
        overlay.classList.remove('is-active');
        overlay.setAttribute('aria-hidden', 'true');
      }
      document.body.style.overflow = '';
    }

    // Open drawer triggers
    qsa('[data-cart-toggle]').forEach(function (btn) {
      on(btn, 'click', function (e) {
        e.preventDefault();
        openDrawer();
      });
    });

    // Close triggers (delegated so they survive innerHTML refreshes)
    on(overlay, 'click', closeDrawer);
    on(drawer, 'click', function (e) {
      if (e.target.closest('[data-cart-drawer-close]')) closeDrawer();
    });
    on(document, 'keydown', function (e) {
      if (e.key === 'Escape' && drawer.classList.contains('is-active')) closeDrawer();
    });

    // Quantity buttons
    on(drawer, 'click', function (e) {
      var minus = e.target.closest('[data-cart-quantity-minus]');
      var plus = e.target.closest('[data-cart-quantity-plus]');
      var remove = e.target.closest('[data-cart-remove]');

      if (minus) {
        var key = minus.dataset.key;
        var valueEl = minus.parentElement.querySelector('[data-cart-quantity-value]');
        var qty = parseInt(valueEl.textContent, 10);
        updateCartLine(key, Math.max(0, qty - 1));
      }

      if (plus) {
        var key2 = plus.dataset.key;
        var valueEl2 = plus.parentElement.querySelector('[data-cart-quantity-value]');
        var qty2 = parseInt(valueEl2.textContent, 10);
        updateCartLine(key2, qty2 + 1);
      }

      if (remove) {
        var key3 = remove.dataset.key;
        updateCartLine(key3, 0);
      }
    });

    function updateCartLine(key, quantity) {
      drawer.classList.add('cart-drawer--loading');
      CartAPI.change(key, quantity).then(function (cart) {
        refreshCartDrawer(cart);
        updateCartCounts(cart.item_count);
      }).catch(function (err) {
        drawer.classList.remove('cart-drawer--loading');
        showCartError(window.theme && window.theme.strings ? window.theme.strings.cartUpdateError : 'Could not update cart. Please try again.');
        console.error('Cart update failed:', err);
      });
    }

    function showCartError(message) {
      var existing = qs('.cart-drawer__error', drawer);
      if (existing) existing.remove();
      var errorEl = document.createElement('div');
      errorEl.className = 'cart-drawer__error';
      errorEl.setAttribute('role', 'alert');
      errorEl.textContent = message;
      var inner = qs('[data-cart-drawer-inner]', drawer);
      if (inner) inner.prepend(errorEl);
      setTimeout(function () { errorEl.remove(); }, 5000);
    }

    var _refreshTimer = null;
    function refreshCartDrawer(cart) {
      // Debounce: avoid overlapping fetches on rapid quantity changes
      clearTimeout(_refreshTimer);
      _refreshTimer = setTimeout(function () {
        fetch('/?section_id=cart-drawer')
          .then(function (r) { return r.text(); })
          .then(function (html) {
            var parser = new DOMParser();
            var doc = parser.parseFromString(html, 'text/html');
            var newContent = qs('[data-cart-drawer-inner]', doc);
            var currentContent = qs('[data-cart-drawer-inner]', drawer);
            if (newContent && currentContent) {
              currentContent.innerHTML = newContent.innerHTML;
            }
            drawer.classList.remove('cart-drawer--loading');
          });
      }, 300);
    }

    // Expose open/close for external use
    window.CartDrawer = {
      open: openDrawer,
      close: closeDrawer,
      refresh: function () {
        CartAPI.get().then(function (cart) {
          refreshCartDrawer(cart);
          updateCartCounts(cart.item_count);
        });
      }
    };
  }

  function updateCartCounts(count) {
    qsa('[data-cart-count]').forEach(function (el) {
      el.textContent = count;
      el.dataset.count = count;
    });
    announce(count + (count === 1 ? ' item' : ' items') + ' in cart');
  }

  /* ==========================================================================
     Product form – AJAX add to cart
     ========================================================================== */

  function initProductForm() {
    qsa('[data-product-form]').forEach(function (form) {
      on(form, 'submit', function (e) {
        e.preventDefault();
        var btn = qs('[data-add-to-cart]', form);
        var btnText = qs('[data-add-to-cart-text]', btn);
        if (!btn || btn.disabled) return;

        var section = form.closest('[data-section-type="product"]');
        var prevText = btnText ? btnText.textContent : '';
        btn.disabled = true;
        btn.classList.add('btn--loading');
        if (btnText) btnText.textContent = '';

        var items = [];
        var builder = section ? qs('[data-bundle-builder]', section) : null;
        var bundleActive = builder && !builder.hidden;

        if (bundleActive) {
          // Multi-box mode: collect variant IDs from visible slot hidden inputs
          var visibleSlots = qsa('[data-bb-slot]:not([hidden])', builder);
          var totalNeeded = visibleSlots.length;
          var itemsMap = {};
          var filledCount = 0;

          qsa('[data-bb-slot-variant]', builder).forEach(function (input) {
            var slotNum = input.getAttribute('data-bb-slot-variant');
            var chip = qs('[data-bb-slot="' + slotNum + '"]', builder);
            if (chip && chip.hidden) return; // skip hidden slots (e.g. slot 3 in 2-box mode)
            if (input.value) {
              filledCount++;
              var vid = input.value;
              if (itemsMap[vid]) { itemsMap[vid]++; } else { itemsMap[vid] = 1; }
            }
          });

          // Guard: don't submit if not all visible slots are filled
          if (filledCount < totalNeeded) {
            btn.disabled = false;
            btn.classList.remove('btn--loading');
            if (btnText) btnText.textContent = 'Select all flavours';
            setTimeout(function () {
              if (btnText) btnText.textContent = prevText;
            }, 3000);
            return;
          }

          Object.keys(itemsMap).forEach(function (id) {
            items.push({ id: parseInt(id, 10), quantity: itemsMap[id] });
          });

          // Include selling_plan if subscribe is selected for the bundle
          var sellingPlanVal = section ? qs('[data-selling-plan-input]', section) : null;
          if (sellingPlanVal && sellingPlanVal.value) {
            var planId = parseInt(sellingPlanVal.value, 10);
            items.forEach(function (item) { item.selling_plan = planId; });
          }
        } else {
          // Single-box mode: use form data
          var formData = new FormData(form);
          items = [{
            id: parseInt(formData.get('id'), 10),
            quantity: parseInt(formData.get('quantity'), 10) || 1
          }];
          var sellingPlan = formData.get('selling_plan');
          if (sellingPlan) items[0].selling_plan = parseInt(sellingPlan, 10);
        }

        CartAPI.add(items).then(function () {
          btn.classList.add('btn--success');
          if (btnText) btnText.textContent = window.theme && window.theme.strings ? window.theme.strings.added : 'Added!';
          setTimeout(function () {
            btn.classList.remove('btn--success');
            if (btnText) btnText.textContent = prevText;
          }, 1500);

          CartAPI.get().then(function (cart) {
            updateCartCounts(cart.item_count);
            if (window.CartDrawer) {
              window.CartDrawer.refresh();
              setTimeout(function () { window.CartDrawer.open(); }, 150);
            }
          });
        }).catch(function (err) {
          console.error('Add to cart failed:', err, 'Items:', items);
          if (btnText) btnText.textContent = err.message || (window.theme && window.theme.strings ? window.theme.strings.error : 'Error - try again');
          setTimeout(function () {
            if (btnText) btnText.textContent = prevText;
          }, 3000);
        }).finally(function () {
          btn.disabled = false;
          btn.classList.remove('btn--loading');
        });
      });
    });
  }

  /* ==========================================================================
     Product variant/option selector
     ========================================================================== */

  function initVariantSelector() {
    var section = qs('[data-section-type="product"]');
    if (!section) return;

    var productJsonEl = qs('[data-product-json]', section);
    if (!productJsonEl) return;

    var product;
    try { product = JSON.parse(productJsonEl.textContent); } catch (e) { return; }

    var variantIdInput = qs('[data-variant-id]', section);
    var priceEl = qs('[data-product-price]', section);
    var atcBtn = qs('[data-add-to-cart]', section);
    var atcText = qs('[data-add-to-cart-text]', section);
    var atcPrice = qs('[data-atc-price]', section);
    var stickyAtcPrice = qs('[data-sticky-atc-price]', section);

    // Listen for option changes
    qsa('[data-option-input]', section).forEach(function (input) {
      on(input, 'change', function () {
        updateVariant();
      });
    });

    function getSelectedOptions() {
      var options = [];
      qsa('[data-option-input]:checked', section).forEach(function (input) {
        options.push(input.value);
      });
      return options;
    }

    function findVariant(selectedOptions) {
      return product.variants.find(function (v) {
        return v.options.every(function (opt, i) {
          return opt === selectedOptions[i];
        });
      });
    }

    function updateVariant() {
      var selectedOptions = getSelectedOptions();
      var variant = findVariant(selectedOptions);
      if (!variant) return;

      // Update hidden id input
      if (variantIdInput) variantIdInput.value = variant.id;

      // Update URL without reload
      var url = new URL(window.location);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);

      // Update price display
      updatePriceDisplay(variant);

      // Update availability
      if (atcBtn) {
        if (variant.available) {
          atcBtn.disabled = false;
          if (atcText) atcText.textContent = window.theme && window.theme.strings
            ? window.theme.strings.addToCart : 'Add to Cart';
        } else {
          atcBtn.disabled = true;
          if (atcText) atcText.textContent = window.theme && window.theme.strings
            ? window.theme.strings.soldOut : 'Sold Out';
        }
      }

      // Update active state on option labels
      qsa('[data-option-input]', section).forEach(function (input) {
        var label = input.closest('label');
        if (label) {
          label.classList.toggle('product__swatch--selected', input.checked);
        }
      });

      // Update gallery to show variant image
      if (variant.featured_image) {
        var mediaId = null;
        product.media.forEach(function (m) {
          if (m.id === variant.featured_image.id || m.src === variant.featured_image.src) {
            mediaId = m.id;
          }
        });
        if (mediaId) scrollGalleryTo(mediaId, section);
      }
    }

    function updatePriceDisplay(variant) {
      if (!priceEl) return;

      var priceHtml = '';
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        priceHtml += '<span class="price--compare"><s>' + formatMoney(variant.compare_at_price) + '</s></span> ';
        priceHtml += '<span class="price--current price--sale">' + formatMoney(variant.price) + '</span>';
      } else {
        priceHtml += '<span class="price--current">' + formatMoney(variant.price) + '</span>';
      }
      priceEl.innerHTML = priceHtml;

      if (atcPrice) atcPrice.textContent = formatMoney(variant.price);
      if (stickyAtcPrice) stickyAtcPrice.textContent = formatMoney(variant.price);
    }
  }

  /* ==========================================================================
     Bundle selector
     ========================================================================== */

  function initBundleSelector() {
    var section = qs('[data-section-type="product"]');
    if (!section) return;

    var bundleSelector = qs('[data-bundle-selector]', section);
    if (!bundleSelector) return;

    var builder = qs('[data-bundle-builder]', section);
    var flavorFieldsets = qsa('.product__option', section);

    qsa('[data-bundle-input]', bundleSelector).forEach(function (input) {
      on(input, 'change', function () {
        var option = input.closest('[data-bundle-option]');
        // Update selected styles
        qsa('[data-bundle-option]', bundleSelector).forEach(function (opt) {
          opt.classList.remove('product__bundle-card--selected');
        });
        if (option) option.classList.add('product__bundle-card--selected');

        var qty = option ? parseInt(option.dataset.bundleQuantity, 10) : 1;

        // Preserve scroll position relative to bundle selector to prevent jump
        var bsRect = bundleSelector.getBoundingClientRect();
        var scrollBefore = window.scrollY;
        var topBefore = bsRect.top;

        // Show/hide bundle builder for multi-box
        if (builder) {
          if (qty > 1) {
            builder.hidden = false;
            flavorFieldsets.forEach(function (f) { f.hidden = true; });
            // Show correct number of slot chips
            qsa('[data-bb-slot]', builder).forEach(function (chip) {
              chip.hidden = parseInt(chip.dataset.bbSlot, 10) > qty;
            });
            // D2: Pre-fill default flavours. C99 (2026-10-07): Box 1 = the flavour of the page you are on, then the
            // D9_MIX order (grapefruit, lime, orange, peach) without repeating it. Picks the first available variant per flavour.
            var pageKey = section.getAttribute('data-flavour-key') || '';
            var mixOrder = ['grapefruit', 'lime', 'orange', 'peach'].filter(function (k) { return k !== pageKey; });
            if (pageKey) mixOrder.unshift(pageKey);
            var DEFAULT_KEYS = { 1: mixOrder[0], 2: mixOrder[1], 3: mixOrder[2] };
            function variantForKey(key) {
              var picks = qsa('[data-bb-pick]', builder);
              for (var i = 0; i < picks.length; i++) {
                var name = (picks[i].dataset.bbFlavorName || '').toLowerCase();
                if (key === 'lime' && (name.indexOf('lime') > -1 || name.indexOf('lemon') > -1)) return picks[i];
                if (key === 'orange' && name.indexOf('orange') > -1) return picks[i];
                if (key === 'grapefruit' && name.indexOf('grapefruit') > -1) return picks[i];
                if (key === 'peach' && name.indexOf('peach') > -1) return picks[i];
              }
              return null;
            }
            for (var s = 1; s <= 3; s++) {
              var slotInput = qs('[data-bb-slot-variant="' + s + '"]', builder);
              if (!slotInput) continue;
              if (s <= qty) {
                var defaultPick = variantForKey(DEFAULT_KEYS[s] || 'grapefruit');
                slotInput.value = defaultPick ? (defaultPick.dataset.bbVariantId || '') : '';
              } else {
                slotInput.value = '';
              }
            }
            // Update slot UI
            if (window._bbUpdateSlots) window._bbUpdateSlots(qty);
            // D2: trigger panel sync after defaults
            if (window._bbSyncPanels) window._bbSyncPanels();
          } else {
            builder.hidden = true;
            flavorFieldsets.forEach(function (f) { f.hidden = false; });
            // Restore original ATC button text when leaving bundle mode
            var atcBtnEl = qs('[data-add-to-cart]', section);
            var atcTextEl = atcBtnEl ? qs('[data-add-to-cart-text]', atcBtnEl) : null;
            if (atcBtnEl && atcTextEl && atcBtnEl.dataset.originalText) {
              atcTextEl.textContent = atcBtnEl.dataset.originalText;
              atcBtnEl.classList.remove('btn--incomplete');
            }
          }
        }

        // Update title and description for bundle mode (like LMNT)
        var titleEl = qs('[data-product-title]', section);
        var descEl = qs('[data-product-flavor-desc]', section);
        if (titleEl) {
          if (qty > 1) {
            titleEl.textContent = 'Repleo ' + qty + '-Box Bundle';
          } else {
            titleEl.textContent = titleEl.dataset.originalTitle || titleEl.textContent;
          }
        }
        if (descEl) {
          if (qty > 1) {
            descEl.textContent = 'Mix & match ' + qty + ' boxes of your favourite Repleo flavours. Pick a different flavour for each box or double up on your favourite.';
          } else {
            descEl.textContent = descEl.dataset.originalDesc || descEl.textContent;
          }
        }

        // Update quantity input for single-box mode
        var qtyInput = qs('[data-quantity-input]', section);
        if (qtyInput && qty === 1) qtyInput.value = 1;

        // Switch subscription default based on pack size
        var subSelector = qs('[data-subscription-selector]', section);
        if (subSelector) {
          var saveBadge = qs('[data-subscription-save-badge]', subSelector);
          var recBadge = qs('[data-subscription-recommended]', subSelector);
          var subscribeOpt = qs('[data-subscription-option="subscribe"]', subSelector);
          var onetimeOpt = qs('[data-subscription-option="onetime"]', subSelector);
          var subscribeRadio = subscribeOpt ? qs('input[type="radio"]', subscribeOpt) : null;
          var onetimeRadio = onetimeOpt ? qs('input[type="radio"]', onetimeOpt) : null;
          var sellingPlanInput = qs('[data-selling-plan-input]', section);

          if (qty > 1) {
            // Multi-pack: default to one-time, hide save badge
            if (saveBadge) saveBadge.style.display = 'none';
            if (recBadge) recBadge.style.display = 'none';
            if (onetimeRadio && subscribeRadio) {
              onetimeRadio.checked = true;
              subscribeRadio.checked = false;
              if (subscribeOpt) subscribeOpt.classList.remove('subscription-selector__option--active');
              if (onetimeOpt) onetimeOpt.classList.add('subscription-selector__option--active');
              if (sellingPlanInput) sellingPlanInput.value = '';
            }
          } else {
            // Single box: restore subscribe default, show badges
            if (saveBadge) saveBadge.style.display = '';
            if (recBadge) recBadge.style.display = '';
            if (subscribeRadio && onetimeRadio) {
              subscribeRadio.checked = true;
              onetimeRadio.checked = false;
              if (subscribeOpt) subscribeOpt.classList.add('subscription-selector__option--active');
              if (onetimeOpt) onetimeOpt.classList.remove('subscription-selector__option--active');
              var sellingPlanSelect = qs('[data-selling-plan-select]', subSelector);
              if (sellingPlanInput && sellingPlanSelect) sellingPlanInput.value = sellingPlanSelect.value;
            }
          }
        }

        // Show/hide "Free shipping" badge on subscription selector for multi-box
        qsa('[data-subscription-shipping]', section).forEach(function (badge) {
          badge.hidden = (qty <= 1);
        });

        // Update ATC button price with bundle pricing (top price stays as 1-box always)
        var bundlePrice = option ? option.dataset.bundlePrice : null;
        var bundleSavings = option ? option.dataset.bundleSavings : null;
        var atcPrice = qs('[data-atc-price]', section);

        if (bundlePrice && atcPrice) atcPrice.textContent = bundlePrice;
        // Sticky-bar price + savings are owned by the buy-box sync() (handles subscription discount) — single source.

        // Show/hide savings on ATC button + sticky ATC
        var atcSavings = qs('[data-atc-savings]', section);
        if (atcSavings) {
          if (bundleSavings) {
            atcSavings.textContent = bundleSavings;
            atcSavings.classList.add('is-visible');
          } else {
            atcSavings.textContent = '';
            atcSavings.classList.remove('is-visible');
          }
        }

        // Update per-serving in subscription selector (both header and details)
        var bundlePerServing = option ? option.dataset.bundlePerServing : null;
        var bundleSubPerServing = option ? option.dataset.bundleSubPerServing : null;
        var onetimePerServing = qs('[data-onetime-per-serving]', section);
        var subPerServing = qs('[data-sub-per-serving]', section);
        var subPerServingHeader = qs('[data-sub-per-serving-header]', section);
        if (bundlePerServing && onetimePerServing) {
          onetimePerServing.textContent = bundlePerServing + '/serving';
        }
        if (bundleSubPerServing && subPerServing) {
          subPerServing.textContent = bundleSubPerServing;
        }
        if (bundleSubPerServing && subPerServingHeader) {
          subPerServingHeader.textContent = bundleSubPerServing + '/serving';
        }

        // Restore scroll position so bundle selector doesn't jump
        requestAnimationFrame(function () {
          var topAfter = bundleSelector.getBoundingClientRect().top;
          var diff = topAfter - topBefore;
          if (Math.abs(diff) > 1) {
            window.scrollTo(0, scrollBefore + diff);
          }
        });
      });
    });
  }

  function initBundleBuilder() {
    var section = qs('[data-section-type="product"]');
    if (!section) return;

    var builder = qs('[data-bundle-builder]', section);
    if (!builder) return;

    // Store original ATC button text so we can restore it when all slots are filled
    var atcBtn = qs('[data-add-to-cart]', section);
    var atcText = atcBtn ? qs('[data-add-to-cart-text]', atcBtn) : null;
    if (atcBtn && atcText) {
      atcBtn.dataset.originalText = atcText.textContent.trim();
    }

    var activeSlot = 1;

    // Click on a slot chip to make it the active slot
    qsa('[data-bb-slot]', builder).forEach(function (chip) {
      on(chip, 'click', function (e) {
        // Don't activate if the X remove button was clicked
        if (e.target.closest('[data-bb-slot-remove]')) return;
        activeSlot = parseInt(chip.dataset.bbSlot, 10);
        refreshSlotUI();
        // Highlight the swatch that matches this slot's current value
        var slotInput = qs('[data-bb-slot-variant="' + activeSlot + '"]', builder);
        highlightPicker(slotInput ? slotInput.value : '');
      });
    });

    // Click X button to remove a flavour from a slot
    qsa('[data-bb-slot-remove]', builder).forEach(function (removeBtn) {
      on(removeBtn, 'click', function (e) {
        e.stopPropagation();
        var chip = removeBtn.closest('[data-bb-slot]');
        if (!chip) return;
        var slotNum = parseInt(chip.dataset.bbSlot, 10);
        var slotInput = qs('[data-bb-slot-variant="' + slotNum + '"]', builder);
        if (slotInput) slotInput.value = '';
        // Make this cleared slot the active slot
        activeSlot = slotNum;
        refreshSlotUI();
        highlightPicker('');
      });
    });

    // Click on a flavour swatch to fill the active slot
    qsa('[data-bb-pick]', builder).forEach(function (btn) {
      on(btn, 'click', function () {
        var variantId = btn.dataset.bbVariantId;

        // Set the active slot's hidden input
        var slotInput = qs('[data-bb-slot-variant="' + activeSlot + '"]', builder);
        if (slotInput) slotInput.value = variantId;

        // Move to next empty slot
        var visibleSlots = qsa('[data-bb-slot]:not([hidden])', builder);
        var totalSlots = visibleSlots.length;
        var movedToEmpty = false;
        for (var i = 1; i <= totalSlots; i++) {
          var checkInput = qs('[data-bb-slot-variant="' + i + '"]', builder);
          if (checkInput && !checkInput.value) {
            activeSlot = i;
            movedToEmpty = true;
            break;
          }
        }
        // If all slots filled, stay on current slot
        if (!movedToEmpty) {
          var nextSlot = activeSlot < totalSlots ? activeSlot + 1 : totalSlots;
          activeSlot = nextSlot;
        }

        refreshSlotUI();
        highlightPicker(qs('[data-bb-slot-variant="' + activeSlot + '"]', builder) ?
          qs('[data-bb-slot-variant="' + activeSlot + '"]', builder).value : '');
      });
    });

    function highlightPicker(variantId) {
      qsa('[data-bb-pick]', builder).forEach(function (btn) {
        btn.classList.toggle('bb__swatch--selected', btn.dataset.bbVariantId === variantId);
      });
    }

    function refreshSlotUI() {
      qsa('[data-bb-slot]', builder).forEach(function (chip) {
        var slotNum = parseInt(chip.dataset.bbSlot, 10);
        var slotInput = qs('[data-bb-slot-variant="' + slotNum + '"]', builder);
        var flavorEl = qs('[data-bb-slot-flavor]', chip);
        var emptyEl = qs('[data-bb-slot-empty]', chip);
        var numEl = qs('.bb__slot-num', chip);
        var imgEl = qs('[data-bb-slot-img]', chip);
        var removeEl = qs('[data-bb-slot-remove]', chip);

        chip.classList.toggle('bb__slot-chip--active', slotNum === activeSlot);

        if (slotInput && slotInput.value) {
          // Find the flavor data from the picker buttons
          var pickBtn = qs('[data-bb-pick][data-bb-variant-id="' + slotInput.value + '"]', builder);
          var name = pickBtn ? pickBtn.dataset.bbFlavorName : '';
          var imgUrl = pickBtn ? pickBtn.dataset.bbFlavorImg : '';

          if (flavorEl) { flavorEl.textContent = name; flavorEl.hidden = false; }
          if (emptyEl) emptyEl.hidden = true;
          chip.classList.add('bb__slot-chip--filled');

          // Show product image, hide slot number
          if (imgEl && imgUrl) {
            imgEl.src = imgUrl;
            imgEl.alt = name;
            imgEl.hidden = false;
          }
          if (numEl) numEl.hidden = true;
          // Show X remove button
          if (removeEl) removeEl.hidden = false;
        } else {
          if (flavorEl) { flavorEl.textContent = ''; flavorEl.hidden = true; }
          if (emptyEl) emptyEl.hidden = false;
          chip.classList.remove('bb__slot-chip--filled');

          // Hide product image, show slot number
          if (imgEl) { imgEl.src = ''; imgEl.hidden = true; }
          if (numEl) numEl.hidden = false;
          // Hide X remove button
          if (removeEl) removeEl.hidden = true;
        }
      });

      // Update progress counter
      var progressEl = qs('[data-bb-progress]', builder);
      var visibleSlots = qsa('[data-bb-slot]:not([hidden])', builder);
      var totalSlots = visibleSlots.length;
      var filledSlots = 0;
      visibleSlots.forEach(function (c) {
        if (c.classList.contains('bb__slot-chip--filled')) filledSlots++;
      });
      if (progressEl) {
        progressEl.textContent = filledSlots + ' of ' + totalSlots + ' selected';
        if (filledSlots >= totalSlots) {
          progressEl.classList.add('bb__slots-progress--complete');
        } else {
          progressEl.classList.remove('bb__slots-progress--complete');
        }
      }

      // Update ATC button text based on whether all slots are filled
      var atcBtn = section ? qs('[data-add-to-cart]', section) : null;
      var atcText = atcBtn ? qs('[data-add-to-cart-text]', atcBtn) : null;
      if (atcText && builder && !builder.hidden) {
        var remaining = totalSlots - filledSlots;
        if (remaining > 0) {
          atcText.textContent = 'Choose ' + remaining + ' more flavour' + (remaining > 1 ? 's' : '');
          atcBtn.classList.add('btn--incomplete');
        } else {
          // Restore original text (stored on first run)
          atcText.textContent = atcBtn.dataset.originalText || 'Add to Cart';
          atcBtn.classList.remove('btn--incomplete');
        }
      }
    }

    // Expose for bundle selector to call when switching pack sizes
    window._bbUpdateSlots = function (qty) {
      activeSlot = 1;
      // Show/hide slot chips based on qty
      for (var s = 1; s <= 3; s++) {
        var chip = qs('[data-bb-slot="' + s + '"]', builder);
        if (!chip) continue;
        chip.hidden = s > qty;
      }
      refreshSlotUI();
      // Clear picker highlight (all slots empty)
      highlightPicker('');
    };
  }

  /* ==========================================================================
     Product details horizontal tabs
     ========================================================================== */

  function initProductDetailsTabs() {
    /* PDP accordion — close siblings when one opens (exclusive-open) */
    qsa('.product__accordion-item').forEach(function (details) {
      var summary = qs('summary', details);
      if (!summary) return;
      on(summary, 'click', function () {
        if (details.open) return; // closing, not opening
        var parent = details.parentElement;
        if (!parent) return;
        qsa('.product__accordion-item', parent).forEach(function (sibling) {
          if (sibling !== details && sibling.open) sibling.open = false;
        });
      });
    });
  }

  /* ==========================================================================
     Subscription toggle
     ========================================================================== */

  function initSubscriptionSelector() {
    qsa('[data-subscription-selector]').forEach(function (selector) {
      var options = qsa('[data-subscription-option]', selector);
      var sellingPlanInput = qs('[data-selling-plan-input]');
      var sellingPlanSelect = qs('[data-selling-plan-select]', selector);

      options.forEach(function (option) {
        var radio = qs('input[type="radio"]', option);
        on(radio, 'change', function () {
          // Update active class
          options.forEach(function (o) { o.classList.remove('subscription-selector__option--active'); });
          option.classList.add('subscription-selector__option--active');

          // Update selling plan hidden input
          if (sellingPlanInput) {
            if (radio.value === 'subscribe' && sellingPlanSelect) {
              sellingPlanInput.value = sellingPlanSelect.value;
            } else {
              sellingPlanInput.value = '';
            }
          }
        });
      });

      // Frequency select change
      if (sellingPlanSelect) {
        on(sellingPlanSelect, 'change', function () {
          if (sellingPlanInput) sellingPlanInput.value = sellingPlanSelect.value;
        });
      }
    });
  }

  /* ==========================================================================
     Quantity selector (+/- buttons)
     ========================================================================== */

  function initQuantitySelectors() {
    qsa('[data-quantity-minus]').forEach(function (btn) {
      on(btn, 'click', function () {
        var input = btn.parentElement.querySelector('[data-quantity-input]');
        if (input) {
          var val = parseInt(input.value, 10);
          input.value = Math.max(1, val - 1);
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    });

    qsa('[data-quantity-plus]').forEach(function (btn) {
      on(btn, 'click', function () {
        var input = btn.parentElement.querySelector('[data-quantity-input]');
        if (input) {
          var val = parseInt(input.value, 10);
          input.value = val + 1;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    });
  }

  /* ==========================================================================
     Product image gallery
     ========================================================================== */

  function initProductGallery() {
    var gallery = qs('[data-product-gallery]');
    if (!gallery) return;

    var main = qs('[data-gallery-main]', gallery);
    var thumbs = qsa('[data-gallery-thumb]', gallery);
    var dots = qsa('[data-gallery-dot]', gallery);
    var slides = qsa('[data-gallery-slide]', gallery);
    var currentIndex = 0;

    function goToSlide(index) {
      if (index < 0 || index >= slides.length) return;
      currentIndex = index;

      slides.forEach(function (slide, i) {
        slide.hidden = i !== index;
        slide.setAttribute('aria-hidden', i !== index ? 'true' : 'false');
      });

      thumbs.forEach(function (thumb, i) {
        thumb.classList.toggle('product__gallery-thumbnail--active', i === index);
        if (i === index) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });

      dots.forEach(function (dot, i) {
        dot.classList.toggle('product__gallery-dot--active', i === index);
        dot.setAttribute('aria-current', i === index ? 'true' : 'false');
      });
    }

    // Thumbnail clicks
    thumbs.forEach(function (thumb, i) {
      on(thumb, 'click', function () { goToSlide(i); });
    });

    // Dot clicks
    dots.forEach(function (dot, i) {
      on(dot, 'click', function () { goToSlide(i); });
    });

    // Touch swipe support on main gallery
    var touchStartX = 0;
    var touchEndX = 0;

    on(main, 'touchstart', function (e) {
      touchStartX = e.changedTouches[0].screenX;
    }, { passive: true });

    on(main, 'touchend', function (e) {
      touchEndX = e.changedTouches[0].screenX;
      var diff = touchStartX - touchEndX;
      if (Math.abs(diff) > 50) {
        if (diff > 0 && currentIndex < slides.length - 1) {
          goToSlide(currentIndex + 1);
        } else if (diff < 0 && currentIndex > 0) {
          goToSlide(currentIndex - 1);
        }
      }
    }, { passive: true });

    // Expose for variant selector
    gallery._goToSlide = goToSlide;
    gallery._slides = slides;
  }

  function scrollGalleryTo(mediaId, section) {
    var gallery = qs('[data-product-gallery]', section);
    if (!gallery || !gallery._slides) return;

    gallery._slides.forEach(function (slide, i) {
      if (slide.dataset.mediaId == mediaId) {
        gallery._goToSlide(i);
      }
    });
  }

  /* ==========================================================================
     Sticky ATC bar (mobile)
     ========================================================================== */

  function initStickyATC() {
    var stickyBar = qs('[data-sticky-atc]');
    if (!stickyBar) return;

    var atcWrapper = qs('[data-add-to-cart-wrapper]');
    if (!atcWrapper) return;

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) {
          stickyBar.hidden = false;
          requestAnimationFrame(function () { stickyBar.classList.add('is-visible'); });
        } else {
          stickyBar.classList.remove('is-visible');
          setTimeout(function () { stickyBar.hidden = true; }, 300);
        }
      });
    }, { threshold: 0 });

    observer.observe(atcWrapper);

    // Sticky bar button triggers main form submit
    var stickyBtn = qs('[data-sticky-atc-button]', stickyBar);
    if (stickyBtn) {
      on(stickyBtn, 'click', function () {
        var form = qs('[data-product-form]');
        if (form) form.requestSubmit ? form.requestSubmit() : form.dispatchEvent(new Event('submit'));
      });
    }
  }

  /* ==========================================================================
     Money formatting
     ========================================================================== */

  function formatMoney(cents) {
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents, window.theme && window.theme.moneyFormat);
    }
    // Fallback: format as currency with 2 decimals
    var amount = (cents / 100).toFixed(2);
    var format = window.theme && window.theme.moneyFormat
      ? window.theme.moneyFormat
      : '{{amount}}';
    return format
      .replace('{{amount}}', amount)
      .replace('{{amount_no_decimals}}', Math.round(cents / 100))
      .replace('{{amount_with_comma_separator}}', amount.replace('.', ','));
  }

  /* ==========================================================================
     Share button
     ========================================================================== */

  function initShareButton() {
    qsa('[data-share-button]').forEach(function (btn) {
      on(btn, 'click', function () {
        if (navigator.share) {
          navigator.share({
            title: document.title,
            url: window.location.href
          }).catch(function () {});
        } else {
          // Fallback: copy to clipboard
          navigator.clipboard.writeText(window.location.href).then(function () {
            var original = btn.textContent;
            btn.textContent = window.theme && window.theme.strings ? window.theme.strings.linkCopied : '✓ Copied!';
            setTimeout(function () { btn.textContent = original; }, 2000);
          });
        }
      });
    });
  }

  /* ==========================================================================
     Magnetic button hover effect
     Desktop only - buttons subtly follow cursor proximity
     ========================================================================== */

  function initMagneticButtons() {
    // Only on desktop with pointer device
    if (window.matchMedia('(pointer: coarse)').matches) return;
    if (document.documentElement.classList.contains('reduce-motion')) return;

    var magnetStrength = 0.3; // how far the button shifts (fraction of distance)
    var magnetRadius = 80;    // px radius of the magnetic effect

    qsa('.btn--primary, .btn--accent, .btn--lg').forEach(function (btn) {
      // Skip buttons inside cart drawer or mobile elements
      if (btn.closest('.cart-drawer') || btn.closest('.mobile-menu')) return;

      var cachedRect = null;

      on(btn, 'mouseenter', function () {
        cachedRect = btn.getBoundingClientRect();
      });

      on(btn, 'mousemove', function (e) {
        if (!cachedRect) return;
        var cx = cachedRect.left + cachedRect.width / 2;
        var cy = cachedRect.top + cachedRect.height / 2;
        var dx = e.clientX - cx;
        var dy = e.clientY - cy;
        var dist = Math.sqrt(dx * dx + dy * dy);

        if (dist < magnetRadius) {
          var pull = (1 - dist / magnetRadius) * magnetStrength;
          btn.style.transform = 'translate(' + (dx * pull) + 'px, ' + (dy * pull - 1) + 'px)';
        }
      });

      on(btn, 'mouseleave', function () {
        btn.style.transform = '';
        cachedRect = null;
      });
    });
  }

  /* ==========================================================================
     Smooth counter animation (for cart count badge)
     ========================================================================== */

  function initCartCountBounce() {
    // Add a subtle scale-bounce when cart count changes
    var counts = qsa('[data-cart-count]');
    if (!counts.length) return;

    var originalUpdate = updateCartCounts;
    updateCartCounts = function (count) {
      originalUpdate(count);
      counts.forEach(function (el) {
        el.style.transform = 'scale(1.3)';
        el.style.transition = 'transform 300ms cubic-bezier(0.34, 1.56, 0.64, 1)';
        setTimeout(function () {
          el.style.transform = 'scale(1)';
        }, 200);
      });
    };
  }

  /* ==========================================================================
     Upsell "Add to Cart" handler
     ========================================================================== */

  function initUpsellAdd() {
    var drawer = qs('#cart-drawer');
    if (!drawer) return;
    // refreshCartDrawer() is private to initCartDrawer — go through the public
    // window.CartDrawer.refresh() (re-renders the drawer + updates [data-cart-count]).
    function refreshDrawerAndCount() {
      if (window.CartDrawer && window.CartDrawer.refresh) {
        window.CartDrawer.refresh();
      } else {
        CartAPI.get().then(function (c) { updateCartCounts(c.item_count); });
      }
    }
    on(drawer, 'click', function(e) {
      // Volume-upsell flavour picker — add the chosen flavour box, then refresh.
      var flavourBtn = e.target.closest('[data-cart-flavour-add]');
      if (flavourBtn) {
        e.preventDefault();
        var fvid = flavourBtn.getAttribute('data-cart-flavour-add');
        if (!fvid) return;
        flavourBtn.classList.add('is-adding');
        CartAPI.add([{ id: parseInt(fvid, 10), quantity: 1 }]).then(function () {
          refreshDrawerAndCount();
        }).catch(function (err) {
          console.error('Flavour add error:', err);
          flavourBtn.classList.remove('is-adding');
        });
        return;
      }
      var btn = e.target.closest('[data-upsell-add]');
      if (!btn) return;
      e.preventDefault();
      var variantId = btn.getAttribute('data-upsell-add') || btn.getAttribute('data-variant-id');
      if (!variantId) return;
      btn.disabled = true;
      btn.textContent = 'Adding...';
      CartAPI.add([{ id: parseInt(variantId, 10), quantity: 1 }]).then(function() {
        refreshDrawerAndCount();
      }).catch(function(err) {
        console.error('Upsell add error:', err);
        btn.disabled = false;
        btn.textContent = 'Add';
      });
    });
  }

  /* ==========================================================================
     Initialize everything on DOMContentLoaded
     ========================================================================== */

  function initCartCount() {
    // Cart count is rendered server-side in header.liquid
    // No additional fetch needed on page load
  }

  function init() {
    initMobileMenu();
    initStickyHeader();
    initCountrySelector();
    initCartDrawer();
    initProductForm();
    initVariantSelector();
    initBundleSelector();
    initBundleBuilder();
    initSubscriptionSelector();
    initProductDetailsTabs();
    initQuantitySelectors();
    initProductGallery();
    initStickyATC();
    initShareButton();
    initMagneticButtons();
    initCartCountBounce();
    initCartCount();
    initUpsellAdd();
  }

  if (document.readyState === 'loading') {
    on(document, 'DOMContentLoaded', init);
  } else {
    init();
  }

})();

/* ============================================================
   D2 mobile carousel — horizontal scroll + prev/next arrows
   Wires any .d2-mobile-carousel paired with a sibling .d2-carousel-nav
   containing [data-carousel-prev] and [data-carousel-next] buttons.
   Idempotent: safe to call multiple times.
   ============================================================ */
(function () {
  'use strict';

  function initD2Carousels() {
    var tracks = document.querySelectorAll('.d2-mobile-carousel:not([data-d2-inited])');
    tracks.forEach(function (track) {
      track.setAttribute('data-d2-inited', '1');
      var wrapper = track.closest('.d2-carousel-wrapper') || track.parentElement;
      if (!wrapper) return;
      var nav = wrapper.querySelector('.d2-carousel-nav');
      if (!nav) return;
      var prev = nav.querySelector('[data-carousel-prev]');
      var next = nav.querySelector('[data-carousel-next]');
      var loop = track.hasAttribute('data-carousel-loop');

      // Infinite loop (opt-in via data-carousel-loop): clone the full set on both
      // sides and recenter on the real set, so the carousel starts mid-stream and
      // never dead-ends in either direction. Clones are aria-hidden (screen readers
      // only hear the real items) and hidden by CSS once the desktop grid takes over.
      if (loop && track.children.length > 1) {
        var originals = Array.prototype.slice.call(track.children);
        var headFrag = document.createDocumentFragment();
        var tailFrag = document.createDocumentFragment();
        originals.forEach(function (node) {
          var h = node.cloneNode(true);
          var t = node.cloneNode(true);
          h.setAttribute('aria-hidden', 'true'); h.setAttribute('data-clone', '1');
          t.setAttribute('aria-hidden', 'true'); t.setAttribute('data-clone', '1');
          headFrag.appendChild(h);
          tailFrag.appendChild(t);
        });
        track.insertBefore(headFrag, track.firstChild);
        track.appendChild(tailFrag);

        var setWidth = function () { return track.scrollWidth / 3; };
        var recenter = function () { track.scrollLeft = setWidth(); };
        var jumping = false;
        track.addEventListener('scroll', function () {
          if (jumping) return;
          var w = setWidth();
          if (track.scrollLeft <= w * 0.5) {
            jumping = true; track.scrollLeft += w;
            requestAnimationFrame(function () { jumping = false; });
          } else if (track.scrollLeft >= w * 2.5) {
            jumping = true; track.scrollLeft -= w;
            requestAnimationFrame(function () { jumping = false; });
          }
        }, { passive: true });
        requestAnimationFrame(recenter);
        window.addEventListener('resize', recenter);
      }

      function scrollStep() {
        var firstCard = track.querySelector(':scope > *');
        var step = firstCard ? firstCard.getBoundingClientRect().width + 14 : track.clientWidth * 0.85;
        return step;
      }

      function updateButtons() {
        if (loop || !prev || !next) return; // looping carousels never disable their arrows
        var max = track.scrollWidth - track.clientWidth - 2;
        prev.disabled = track.scrollLeft <= 2;
        next.disabled = track.scrollLeft >= max;
      }

      if (prev) prev.addEventListener('click', function () {
        track.scrollBy({ left: -scrollStep(), behavior: 'smooth' });
      });
      if (next) next.addEventListener('click', function () {
        track.scrollBy({ left: scrollStep(), behavior: 'smooth' });
      });
      track.addEventListener('scroll', updateButtons, { passive: true });
      window.addEventListener('resize', updateButtons);
      updateButtons();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initD2Carousels);
  } else {
    initD2Carousels();
  }
  // Re-run when Shopify theme editor injects new sections
  document.addEventListener('shopify:section:load', initD2Carousels);
})();

/* ============================================================
   Random flavour CTAs — point shop/CTA buttons at a randomly
   chosen flavour PDP (one pick per page load, applied to every
   [data-flavour-cta] element). The Liquid href stays as a no-JS
   fallback (collection page) and is overwritten here.
   Idempotent: safe to call multiple times.
   ============================================================ */
(function () {
  'use strict';

  function initRandomFlavourCtas() {
    var flavours = ['/products/lemon-lime', '/products/orange', '/products/grapefruit', '/products/white-peach'];
    var pick = flavours[Math.floor(Math.random() * flavours.length)];
    var ctas = document.querySelectorAll('[data-flavour-cta]');
    if (!ctas.length) return;
    ctas.forEach(function (el) {
      el.setAttribute('href', pick);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initRandomFlavourCtas);
  } else {
    initRandomFlavourCtas();
  }
  // Re-run when Shopify theme editor injects new sections
  document.addEventListener('shopify:section:load', initRandomFlavourCtas);
})();
